import { ReactNode, useEffect, useState } from 'react';
import { Banner, Page, Spinner, Text } from '@shopify/polaris';

interface EmbeddedAppProviderProps {
  children: ReactNode;
}

/**
 * EmbeddedAppProvider
 *
 * Handles the embedded app context and authentication state.
 * - Checks if running in Shopify Admin (embedded context)
 * - Verifies shop installation
 * - Redirects to OAuth if not installed
 */
export function EmbeddedAppProvider({ children }: EmbeddedAppProviderProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [isEmbedded, setIsEmbedded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    checkEmbeddedContext();
  }, []);

  async function checkEmbeddedContext() {
    try {
      // Check if App Bridge is available
      if (window.shopify?.environment?.embedded) {
        setIsEmbedded(true);

        // Verify we can get a session token
        try {
          await window.shopify.idToken();
        } catch {
          // If we can't get a token, the shop may need to re-authenticate
          console.warn('Could not get session token');
        }
      } else {
        // Running standalone (not embedded)
        // Check URL params for shop
        const urlParams = new URLSearchParams(window.location.search);
        const shop = urlParams.get('shop');
        const host = urlParams.get('host');

        if (shop && host) {
          // We have shop info, verify installation
          const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
          try {
            const response = await fetch(`${apiBaseUrl}/auth/verify?shop=${shop}`);
            const data = await response.json();

            if (!data.installed) {
              // Redirect to OAuth
              window.location.href = `${apiBaseUrl}/auth?shop=${shop}`;
              return;
            }
          } catch {
            console.warn('Could not verify shop installation');
          }
        }
      }

      setIsLoading(false);
    } catch (err) {
      console.error('Error checking embedded context:', err);
      setError('Failed to initialize app');
      setIsLoading(false);
    }
  }

  // Loading state
  if (isLoading) {
    return (
      <Page>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '100px 0' }}>
          <Spinner size="large" />
        </div>
      </Page>
    );
  }

  // Error state
  if (error) {
    return (
      <Page title="Error">
        <Banner tone="critical">
          <p>{error}</p>
        </Banner>
      </Page>
    );
  }

  // Development mode notice (not embedded)
  if (!isEmbedded && import.meta.env.DEV) {
    return (
      <>
        <div style={{ padding: '8px', background: '#ffc107', textAlign: 'center' }}>
          <Text as="span" variant="bodySm">
            Development Mode - Not embedded in Shopify Admin
          </Text>
        </div>
        {children}
      </>
    );
  }

  // Production mode - block access if not embedded in Shopify Admin
  if (!isEmbedded) {
    return (
      <Page title="Access Denied">
        <Banner tone="critical">
          <p>This application can only be accessed through the Shopify Admin dashboard.</p>
          <p>Please open this app from your Shopify Admin to continue.</p>
        </Banner>
      </Page>
    );
  }

  return <>{children}</>;
}

/**
 * Hook to check if running in embedded context
 */
export function useEmbedded(): boolean {
  return window.shopify?.environment?.embedded ?? false;
}

/**
 * Hook to get shop info from App Bridge
 */
export function useShopInfo(): { shop: string; host: string } | null {
  if (!window.shopify?.config) {
    return null;
  }
  return {
    shop: window.shopify.config.shop,
    host: window.shopify.config.host,
  };
}

/**
 * Hook to show toast notifications via App Bridge
 */
export function useToast() {
  return {
    show: (message: string, isError = false) => {
      if (window.shopify?.toast) {
        window.shopify.toast.show(message, { isError });
      } else {
        // Fallback for non-embedded context
        if (isError) {
          console.error(message);
        } else {
          console.log(message);
        }
      }
    },
  };
}
